import { Carousel } from "antd";
import { useState, useRef } from "react";
import { IMAGE_NOT_FOUND } from "constants/common";
import { CarouselRef } from "antd/lib/carousel";
import ProductImage from "components/ProductImage";

interface MobileProductImageGalleryProps {
  images: string[];
  productName: string;
  productCode: string;
  isOutOfStock?: boolean;
  className?: string;
}

const MobileProductImageGallery: React.FC<MobileProductImageGalleryProps> = ({
  images,
  productName,
  productCode,
  isOutOfStock = false,
  className = "",
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<CarouselRef>(null);

  // Handle case where there are no images
  const displayImages =
    images && images.length > 0 ? images : [IMAGE_NOT_FOUND];

  const handleThumbnailClick = (index: number) => {
    carouselRef.current?.goTo(index);
    setCurrentSlide(index);
  };

  const handleSlideChange = (_from: number, to: number) => {
    setCurrentSlide(to);
  };

  // Navigation functions
  const goToPrevious = () => {
    if (displayImages.length > 1) {
      const prevIndex =
        currentSlide === 0 ? displayImages.length - 1 : currentSlide - 1;
      carouselRef.current?.goTo(prevIndex);
    }
  };

  const goToNext = () => {
    if (displayImages.length > 1) {
      const nextIndex =
        currentSlide === displayImages.length - 1 ? 0 : currentSlide + 1;
      carouselRef.current?.goTo(nextIndex);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Carousel */}
      <div className="mb-3 relative">
        <Carousel
          ref={carouselRef}
          autoplay={false}
          dots={false}
          beforeChange={handleSlideChange}
          swipeToSlide
          touchThreshold={10}
          className="rounded-lg overflow-hidden bg-gray-50"
        >
          {displayImages.map((image, index) => (
            <div key={index}>
              <div className="h-[350px] flex items-center justify-center bg-gray-50">
                <ProductImage
                  imageUrl={image}
                  productCode={productCode}
                  height={350}
                  alt={`${productName} ${index + 1}`}
                  className="object-contain w-full"
                  fallback={IMAGE_NOT_FOUND}
                />
              </div>
            </div>
          ))}
        </Carousel>

        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            {/* Left Arrow */}
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 z-20 cursor-pointer"
              aria-label="Previous image"
            >
              <svg
                className="w-4 h-4 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Right Arrow */}
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 z-20 cursor-pointer"
              aria-label="Next image"
            >
              <svg
                className="w-4 h-4 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Thumbnail Images - Show only if more than one image */}
      {displayImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto p-2 px-1 justify-center">
          {displayImages.map((image, index) => (
            <div
              key={index}
              className={`flex-shrink-0 cursor-pointer border-2 rounded-lg overflow-hidden transition-all duration-300 ${
                currentSlide === index
                  ? "border-primary-700 ring-1 ring-primary-300 shadow-md scale-110"
                  : "border-gray-200"
              }`}
              onClick={() => handleThumbnailClick(index)}
            >
              <ProductImage
                imageUrl={image}
                productCode={productCode}
                width={60}
                height={60}
                alt={`${productName} thumbnail ${index + 1}`}
                className="object-contain bg-white"
                fallback={IMAGE_NOT_FOUND}
                preview={false}
              />
            </div>
          ))}
        </div>
      )}

      {/* Custom Dots Indicator - Show current position */}
      {displayImages.length > 1 && (
        <div className="flex justify-center mt-2 gap-2">
          {displayImages.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentSlide === index
                  ? "bg-primary-700 scale-125"
                  : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      )}

      {/* Out of Stock Overlay */}
      {isOutOfStock && (
        <div className="absolute w-1/3 h-1/3 bg-[#35373cba] flex items-center justify-center rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="text-white font-medium text-sm">สินค้าหมด</div>
        </div>
      )}
    </div>
  );
};

export default MobileProductImageGallery;
